import type { BrandConnection, BrandUser } from "./brandPrototypeModel";

const normalizeIdentity = (value: string | null | undefined) => value?.trim().toLowerCase() || "";

export const getBrandConnectionOwner = (connection: BrandConnection, users: BrandUser[]) => {
  const connectedBy = normalizeIdentity(connection.connectedByUserId);
  if (!connectedBy) {
    return null;
  }

  return (
    users.find((user) => [user.id, user.email].some((identity) => normalizeIdentity(identity) === connectedBy)) || null
  );
};

/**
 * Member-facing resource contracts deliberately omit the connector's user id
 * and email. The backend supplies this display-only name so the UI can render
 * connector attribution without turning it into an authorization identity.
 */
export const getBrandConnectionOwnerDisplayName = (
  connection: BrandConnection,
  users: BrandUser[],
) => {
  const owner = getBrandConnectionOwner(connection, users);
  return owner?.name || owner?.email || connection.connectedByName?.trim() || null;
};

export const isBrandConnectionOwner = ({
  connection,
  users,
  userId,
}: {
  connection: BrandConnection;
  users: BrandUser[];
  userId: string;
}) => {
  const targetIdentity = normalizeIdentity(userId);
  if (!targetIdentity) {
    return false;
  }

  const connectionOwner = getBrandConnectionOwner(connection, users);
  return [connection.connectedByUserId, connectionOwner?.id, connectionOwner?.email].some(
    (identity) => normalizeIdentity(identity) === targetIdentity,
  );
};

export const canManageBrandConnectionAccess = ({
  connection,
  users,
  currentUserIds,
  isWorkspaceOwner = false,
  hasIntegrationManagementPermission = false,
}: {
  connection: BrandConnection;
  users: BrandUser[];
  currentUserIds: string[];
  isWorkspaceOwner?: boolean;
  hasIntegrationManagementPermission?: boolean;
}) => {
  if (
    isWorkspaceOwner ||
    hasIntegrationManagementPermission ||
    connection.canManageConnection ||
    connection.canManageAccess
  ) {
    return true;
  }

  const currentIdentities = new Set(currentUserIds.map(normalizeIdentity).filter(Boolean));
  if (currentIdentities.has(normalizeIdentity(connection.connectedByUserId))) {
    return true;
  }

  return currentUserIds.some((userId) => isBrandConnectionOwner({ connection, users, userId }));
};
